/**
 * ScreenshotShareDialogComponent
 *
 * Multi-tab share dialog (Share + Activity). Single FormGroup `shareForm` with
 * 13 controls drives every input. `send()` collects `shareForm.value` and
 * routes through ScreenshotsService.shareData (mock today, real API later).
 *
 * Activity tab flattens `screenshot.shares[]` across all selected screenshots
 * for a unified history view.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import {
  Contact,
  Screenshot,
  ScreenshotShareDialogData,
  ShareAccess,
  ShareChannel,
  ShareExpiration,
  ShareFormValue,
  ShareRecord,
} from '../screenshots.types';
import {
  ACCESS_OPTIONS,
  EXPIRATION_OPTIONS,
} from '../screenshots.constants';
import { ScreenshotsService } from '../screenshots.service';

interface ShareFormGroupShape {
  channel: FormControl<ShareChannel>;
  recipients: FormControl<Contact[]>;
  recipientSearch: FormControl<string>;
  message: FormControl<string>;
  requireResponse: FormControl<boolean>;
  access: FormControl<ShareAccess>;
  password: FormControl<string>;
  expiration: FormControl<ShareExpiration>;
  allowDownload: FormControl<boolean>;
  allowComments: FormControl<boolean>;
  includeAnnotations: FormControl<boolean>;
  includeLinkedMeasurement: FormControl<boolean>;
}

@Component({
  standalone: false,
  selector: 'app-screenshot-share-dialog',
  templateUrl: './screenshot-share-dialog.component.html',
  styleUrls: ['./screenshot-share-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeScale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.96)' }),
        animate('180ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class ScreenshotShareDialogComponent implements OnInit, OnDestroy {
  public readonly screenshots: Screenshot[];

  public readonly shareForm = new FormGroup<ShareFormGroupShape>({
    channel: new FormControl<ShareChannel>('link', { nonNullable: true }),
    recipients: new FormControl<Contact[]>([], { nonNullable: true }),
    recipientSearch: new FormControl<string>('', { nonNullable: true }),
    message: new FormControl<string>('', { nonNullable: true }),
    requireResponse: new FormControl<boolean>(false, { nonNullable: true }),
    access: new FormControl<ShareAccess>('anyone', { nonNullable: true }),
    password: new FormControl<string>(this.randomPassword(), { nonNullable: true }),
    expiration: new FormControl<ShareExpiration>('7d', { nonNullable: true }),
    allowDownload: new FormControl<boolean>(true, { nonNullable: true }),
    allowComments: new FormControl<boolean>(true, { nonNullable: true }),
    includeAnnotations: new FormControl<boolean>(true, { nonNullable: true }),
    includeLinkedMeasurement: new FormControl<boolean>(true, { nonNullable: true }),
  });

  public readonly accessOptions = ACCESS_OPTIONS;
  public readonly expirationOptions = EXPIRATION_OPTIONS;

  public activeTab: 'share' | 'activity' = 'share';
  public successScreen = false;
  public lastShareResult?: ShareRecord;
  public copyFlash = false;
  public contactSuggestions: Contact[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly dialogRef: MatDialogRef<ScreenshotShareDialogComponent, ShareRecord | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: ScreenshotShareDialogData,
    private readonly screenshotsService: ScreenshotsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.screenshots = data.screenshots ?? [];
  }

  ngOnInit(): void {
    // Recipient typeahead — service returns mocked contacts today.
    this.shareForm.controls.recipientSearch.valueChanges
      .pipe(
        debounceTime(160),
        switchMap((q) => this.screenshotsService.searchContacts(q ?? '')),
        takeUntil(this.destroy$)
      )
      .subscribe((list) => {
        const taken = new Set(this.shareForm.controls.recipients.value.map((r) => r.id));
        this.contactSuggestions = list.filter((c) => !taken.has(c.id)).slice(0, 8);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Tab switching =====

  switchTab(tab: 'share' | 'activity'): void {
    this.activeTab = tab;
  }

  // ===== Recipients =====

  addRecipient(contact: Contact): void {
    const current = this.shareForm.controls.recipients.value;
    if (current.some((c) => c.id === contact.id)) return;
    this.shareForm.controls.recipients.setValue([...current, contact]);
    this.shareForm.controls.recipientSearch.setValue('');
    this.contactSuggestions = [];
  }

  removeRecipient(contact: Contact): void {
    const current = this.shareForm.controls.recipients.value;
    this.shareForm.controls.recipients.setValue(current.filter((c) => c.id !== contact.id));
  }

  // ===== Access controls =====

  setAccess(access: ShareAccess): void {
    this.shareForm.controls.access.setValue(access);
  }

  regeneratePassword(): void {
    this.shareForm.controls.password.setValue(this.randomPassword());
  }

  // ===== Send / close =====

  send(): void {
    // Collect the typed form value (skipping the search-input control).
    const v = this.shareForm.getRawValue();
    const formValue: ShareFormValue = {
      channel: v.channel,
      recipients: v.recipients,
      message: v.message,
      requireResponse: v.requireResponse,
      access: v.access,
      password: v.password,
      expiration: v.expiration,
      allowDownload: v.allowDownload,
      allowComments: v.allowComments,
      includeAnnotations: v.includeAnnotations,
      includeLinkedMeasurement: v.includeLinkedMeasurement,
    };
    const ids = this.screenshots.map((s) => s.id);
    this.screenshotsService
      .shareData(null, ids, formValue)
      .pipe(takeUntil(this.destroy$))
      .subscribe((record) => {
        this.lastShareResult = record;
        // Attach the share record to every selected screenshot
        for (const id of ids) this.screenshotsService.addShareRecord(id, record);
        this.successScreen = true;
        this.cdr.markForCheck();
      });
  }

  copyLink(): void {
    const url = this.lastShareResult?.shareUrl ?? this.previewShareUrl;
    if (!url) return;
    try {
      navigator.clipboard?.writeText(url);
    } catch { /* ignore */ }
    this.copyFlash = true;
    setTimeout(() => {
      this.copyFlash = false;
      this.cdr.markForCheck();
    }, 1500);
  }

  copyPassword(): void {
    const pw = this.lastShareResult?.password ?? this.shareForm.controls.password.value;
    if (!pw) return;
    try {
      navigator.clipboard?.writeText(pw);
    } catch { /* ignore */ }
    this.copyFlash = true;
    setTimeout(() => {
      this.copyFlash = false;
      this.cdr.markForCheck();
    }, 1500);
  }

  shareAgain(): void {
    this.successScreen = false;
    this.lastShareResult = undefined;
  }

  done(): void {
    this.dialogRef.close(this.lastShareResult ?? null);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  // ===== Derived view helpers =====

  /** Preview URL shown before send (illustrative). */
  get previewShareUrl(): string {
    const id = this.screenshots[0]?.id ?? 'preview';
    return `https://takeoff.app/s/${id}-preview`;
  }

  /** All share history flattened across selected screenshots, newest first. */
  get activityFeed(): { screenshot: Screenshot; record: ShareRecord }[] {
    const feed: { screenshot: Screenshot; record: ShareRecord }[] = [];
    for (const s of this.screenshots) {
      for (const r of s.shares ?? []) feed.push({ screenshot: s, record: r });
    }
    return feed.sort((a, b) => b.record.createdAt.localeCompare(a.record.createdAt));
  }

  get activityCount(): number {
    return this.activityFeed.length;
  }

  /** Thumbnails to show in the header strip (first 6). */
  get thumbnailStrip(): Screenshot[] {
    return this.screenshots.slice(0, 6);
  }

  /** Total annotation count for the "include annotations" checkbox label. */
  get totalAnnotationCount(): number {
    return this.screenshots.reduce((sum, s) => sum + (s.annotations?.length ?? 0), 0);
  }

  get hasLinkedMeasurements(): boolean {
    return this.screenshots.some((s) => !!s.linkedMeasurementId);
  }

  get titleText(): string {
    if (this.screenshots.length === 1) return 'Share clip';
    return `Share ${this.screenshots.length} clips`;
  }

  trackByContact(_: number, c: Contact): string {
    return c.id;
  }

  trackByActivity(_: number, item: { record: ShareRecord }): string {
    return item.record.id;
  }

  private randomPassword(): string {
    return Math.random().toString(36).slice(2, 8).toUpperCase();
  }
}
